import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QrCode, MousePointerClick, Monitor, Globe, Smartphone, BarChart3, Trophy, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { useChartTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

const COLORS = ["#6366f1", "#8b5cf6", "#06b6d4", "#f59e0b", "#ef4444", "#22c55e", "#ec4899", "#14b8a6", "#f97316", "#a855f7"];

export default function AnalyticsPage() {
  const chartTheme = useChartTheme();
  const { entitlementsReady, authLoading, entitlements } = useAuth();
  const canAnalytics = entitlementsReady && !!entitlements?.advanced_analytics;
  const [analytics, setAnalytics] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!canAnalytics) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    Promise.all([
      api.get("/analytics"),
      api.get("/analytics/comparison"),
    ]).then(([analyticsRes, comparisonRes]) => {
      setAnalytics(analyticsRes.data);
      setComparison(comparisonRes.data);
      setLoading(false);
    }).catch((err) => {
      if (err?.response?.status === 403) setForbidden(true);
      setLoading(false);
    });
  }, [authLoading, canAnalytics]);

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="auth-loading-shell">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (forbidden || !canAnalytics) {
    return (
      <div className="animate-fade-in" data-testid="analytics-upgrade">
        <h1 className="font-heading text-2xl font-bold mb-2">Analytics</h1>
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <p className="text-muted-foreground">Advanced analytics is available on paid plans.</p>
            <Link to="/dashboard/subscription">
              <Button className="btn-glow" data-testid="analytics-upgrade-btn">Upgrade</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="animate-fade-in">
        <h1 className="font-heading text-2xl font-bold mb-2">Analytics</h1>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No analytics data available yet.
          </CardContent>
        </Card>
      </div>
    );
  }

  const deviceData = Object.entries(analytics.device_stats || {}).map(([name, value]) => ({ name, value }));
  const browserData = Object.entries(analytics.browser_stats || {}).map(([name, value]) => ({ name, value }));
  const osData = Object.entries(analytics.os_stats || {}).map(([name, value]) => ({ name, value }));
  const dailyData = analytics.daily_scans || [];
  const variantsList = comparison?.variants || [];

  const comparisonBarData = variantsList.map((v, i) => ({
    name: v.name.length > 12 ? v.name.slice(0, 12) + '...' : v.name,
    scans: v.total_scans,
    clicks: v.link_clicks,
    engagement: v.engagement_rate,
    fill: COLORS[i % COLORS.length],
  }));

  const bestVariant = variantsList.length > 0 ? variantsList[0] : null;

  return (
    <div className="animate-fade-in space-y-8" data-testid="analytics-page">
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Analytics</h1>
        <p className="text-muted-foreground text-sm">Track your QR code performance</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList data-testid="analytics-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="comparison" data-testid="tab-comparison">
            <BarChart3 size={14} className="mr-1.5" /> Compare Variants
          </TabsTrigger>
        </TabsList>

        {/* ────── Overview Tab ────── */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="analytics-stats">
            <Card className="card-hover">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <QrCode size={20} />
                </div>
                <div>
                  <p className="text-3xl font-heading font-bold">{analytics.total_scans}</p>
                  <p className="text-xs text-muted-foreground">Total Scans</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="p-2.5 rounded-lg bg-violet-500/10 text-violet-400">
                  <MousePointerClick size={20} />
                </div>
                <div>
                  <p className="text-3xl font-heading font-bold">{analytics.total_clicks}</p>
                  <p className="text-xs text-muted-foreground">Link Clicks</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {dailyData.length > 0 && (
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="font-heading text-base font-semibold">Scans Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64" data-testid="scans-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: chartTheme.tickColor }} />
                      <YAxis tick={{ fontSize: 12, fill: chartTheme.tickColor }} />
                      <Tooltip contentStyle={chartTheme.tooltipStyle} />
                      <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Devices", data: deviceData, icon: Smartphone },
              { title: "Browsers", data: browserData, icon: Globe },
              { title: "OS", data: osData, icon: Monitor },
            ].map(({ title, data, icon: Icon }) => (
              <Card key={title} className="card-hover">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
                    <Icon size={16} className="text-primary" /> {title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.length > 0 ? (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                            {data.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={chartTheme.tooltipStyle} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex flex-wrap gap-2 justify-center mt-2">
                        {data.map((d, i) => (
                          <Badge key={d.name} variant="secondary" className="text-xs gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                            {d.name}: {d.value}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {analytics.scans?.length > 0 && (
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="font-heading text-base font-semibold">Recent Scans</CardTitle>
              </CardHeader>
              <CardContent>
                <Table data-testid="scans-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Browser</TableHead>
                      <TableHead>OS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.scans.slice(0, 20).map((scan) => (
                      <TableRow key={scan.id}>
                        <TableCell className="text-sm">{new Date(scan.timestamp).toLocaleString()}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-xs">{scan.device_type}</Badge></TableCell>
                        <TableCell className="text-sm">{scan.browser}</TableCell>
                        <TableCell className="text-sm">{scan.os}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ────── Comparison Tab ────── */}
        <TabsContent value="comparison" className="space-y-6" data-testid="comparison-tab">
          {variantsList.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <BarChart3 className="mx-auto text-muted-foreground mb-4" size={40} />
                <h3 className="font-heading text-lg font-semibold mb-1">No QR Variants to Compare</h3>
                <p className="text-muted-foreground text-sm">Create QR variants in the QR Code section to see performance comparisons here.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Top Performer Banner */}
              {bestVariant && bestVariant.total_scans > 0 && (
                <Card className="border-primary/30 bg-primary/5" data-testid="best-variant-banner">
                  <CardContent className="flex items-center gap-4 p-5">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                      <Trophy size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Top Performer</p>
                      <p className="font-heading font-bold text-lg">{bestVariant.name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">{bestVariant.total_scans} scans</span>
                        <span className="text-xs text-muted-foreground">{bestVariant.link_clicks} clicks</span>
                        <span className="text-xs font-medium text-primary">{bestVariant.engagement_rate}% engagement</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Comparison Table */}
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
                    <TrendingUp size={16} className="text-primary" /> Variant Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table data-testid="comparison-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Variant</TableHead>
                        <TableHead className="text-right">Total Scans</TableHead>
                        <TableHead className="text-right">Link Clicks</TableHead>
                        <TableHead className="text-right">Engagement Rate</TableHead>
                        <TableHead>Style</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {variantsList.map((v, i) => (
                        <TableRow key={v.id} data-testid={`comparison-row-${v.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                              <span className="font-medium text-sm">{v.name}</span>
                              {i === 0 && v.total_scans > 0 && <Trophy size={12} className="text-amber-500" />}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium">{v.total_scans}</TableCell>
                          <TableCell className="text-right font-mono font-medium">{v.link_clicks}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant={v.engagement_rate > 50 ? "default" : v.engagement_rate > 20 ? "secondary" : "outline"} className="text-xs font-mono">
                              {v.engagement_rate}%
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{v.qr_style}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Comparison Bar Chart */}
              {comparisonBarData.length > 0 && (
                <Card className="card-hover">
                  <CardHeader>
                    <CardTitle className="font-heading text-base font-semibold">Scans & Clicks by Variant</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72" data-testid="comparison-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonBarData} barCategoryGap="20%">
                          <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: chartTheme.tickColor }} interval={0} />
                          <YAxis tick={{ fontSize: 12, fill: chartTheme.tickColor }} />
                          <Tooltip contentStyle={chartTheme.tooltipStyle} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar dataKey="scans" fill="#6366f1" name="Scans" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="clicks" fill="#8b5cf6" name="Clicks" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Engagement Rate Chart */}
              {comparisonBarData.length > 0 && (
                <Card className="card-hover">
                  <CardHeader>
                    <CardTitle className="font-heading text-base font-semibold">Engagement Rate (%)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-56" data-testid="engagement-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonBarData} barCategoryGap="25%">
                          <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: chartTheme.tickColor }} interval={0} />
                          <YAxis tick={{ fontSize: 12, fill: chartTheme.tickColor }} domain={[0, 100]} />
                          <Tooltip contentStyle={chartTheme.tooltipStyle} formatter={(val) => [`${val}%`, 'Engagement']} />
                          <Bar dataKey="engagement" radius={[4, 4, 0, 0]}>
                            {comparisonBarData.map((entry, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
